import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CasesContext from '../../context/CasesContext';
import AacnContext from '../../context/AacnContext';
import { PATIENT_FIELDS, normalizeDraft, validateApproveDraft, routeApproveErrorMessage, saveCase, generateMrn } from '../../utils/caseDraft';
import { getCurrentUserEmail } from '../../utils/currentUser';
import { setLastCasesTab } from '../../utils/casesViewState';
import './CaseUpload.css';
import '../admin/SimulationCases.css';
import './CaseCreationChat.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ns-dashboard-api.kabilah.com';

const ACCEPTED = ['.pdf', '.docx', '.doc'];

// Ordered by preference. Leaving mimeType unspecified lets the browser pick an implicit
// default, which in testing came back as an empty string on MediaRecorder.mimeType — an
// ambiguous/malformed container is exactly what produces consistent Whisper hallucination
// (e.g. "you") regardless of what was actually said. Picking an explicitly supported type
// avoids that.
const AUDIO_MIME_TYPE_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

const AUDIO_EXTENSION_BY_MIME_TYPE = {
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/mp4': 'mp4',
};

function pickSupportedAudioMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return undefined;
  return AUDIO_MIME_TYPE_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

function audioFileExtension(mimeType) {
  const base = mimeType?.split(';')[0];
  return AUDIO_EXTENSION_BY_MIME_TYPE[base] || 'webm';
}

function FileChip({ file, onRemove }) {
  return (
    <div className="file-chip">
      <span className="file-chip-icon">📄</span>
      <span className="file-chip-name">{file.name}</span>
      <button className="file-chip-remove" onClick={() => onRemove(file.name)}>×</button>
    </div>
  );
}

function createInitialMessages() {
  return [
    {
      id: 'msg-0',
      role: 'assistant',
      type: 'text',
      content: "Hi! I'll help you build a new simulation case conversationally. First, how would you like to start?",
    },
    { id: 'msg-1', role: 'assistant', type: 'path-choice', resolved: false },
  ];
}

// Basics collected up front always take precedence over whatever the AI or a
// duplicated case supplies, since the user explicitly entered them.
function mergeBasics(draft, basics) {
  if (!basics) return draft;
  return {
    ...draft,
    caseName: basics.caseName || draft.caseName,
    mrn: basics.mrn || draft.mrn,
    objectives: basics.objectives || draft.objectives,
    patient: {
      ...draft.patient,
      name: basics.patientName || draft.patient.name,
      age: basics.age || draft.patient.age,
      gender: basics.gender || draft.patient.gender,
      room: basics.room || draft.patient.room,
    },
  };
}

async function sendChatTurn({ path, situationDescription, selectedIndicatorCodes, filesProcessed, extractedFromFiles, transcript }) {
  const res = await axios.post(`${BACKEND_URL}/api/case-chat/message`, {
    path: path ?? null,
    situationDescription: situationDescription || '',
    selectedIndicatorCodes: selectedIndicatorCodes || [],
    filesProcessed: filesProcessed || [],
    extractedFromFiles: extractedFromFiles ?? null,
    transcript,
  });
  return res.data;
}

const PATH_CHOICE_LABELS = {
  upload: 'Upload Existing Case',
  scratch: 'Build from Scratch',
  duplicate: 'Duplicate Existing Case',
};

function PathChoiceCard({ onChoose }) {
  return (
    <div className="chat-card path-choice-card">
      <button className="path-card path-card-compact" onClick={() => onChoose('upload')}>
        <div className="path-card-icon">📄</div>
        <div className="path-card-body">
          <span className="path-card-title">Upload Existing Case</span>
          <span className="path-card-desc">Upload a PDF or Word document and Kabilah will extract the patient scenario.</span>
        </div>
      </button>
      <button className="path-card path-card-compact" onClick={() => onChoose('scratch')}>
        <div className="path-card-icon">✏️</div>
        <div className="path-card-body">
          <span className="path-card-title">Build from Scratch</span>
          <span className="path-card-desc">Describe the situation and Kabilah will help you generate a full case.</span>
        </div>
      </button>
      <button className="path-card path-card-compact" onClick={() => onChoose('duplicate')}>
        <div className="path-card-icon">🗂️</div>
        <div className="path-card-body">
          <span className="path-card-title">Duplicate Existing Case</span>
          <span className="path-card-desc">Pick one of your existing cases to start from an editable copy.</span>
        </div>
      </button>
    </div>
  );
}

function BasicsFormCard({ onContinue, existingMrns }) {
  const [caseName, setCaseName] = useState('');
  const [mrn] = useState(() => generateMrn(existingMrns));
  const [patientName, setPatientName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [room, setRoom] = useState('');

  const canContinue = caseName.trim() && room.trim();

  return (
    <div className="chat-card basics-form-card">
      <div className="review-section">
        <label className="review-field-label">Case Name <span className="review-required">*</span></label>
        <input
          className="review-name-input"
          value={caseName}
          onChange={(e) => setCaseName(e.target.value)}
          placeholder="Enter case name"
        />
      </div>
      <div className="review-section">
        <label className="review-field-label">MRN <span className="review-field-note">auto-generated — unique</span></label>
        <input
          className="review-name-input review-field-readonly"
          value={mrn}
          readOnly
        />
      </div>
      <div className="review-fields-grid">
        <div className="review-editable-field">
          <label className="review-field-key">Patient Name</label>
          <input className="review-field-input" value={patientName} onChange={(e) => setPatientName(e.target.value)} />
        </div>
        <div className="review-editable-field">
          <label className="review-field-key">Age</label>
          <input className="review-field-input" type="number" min="0" value={age} onChange={(e) => setAge(e.target.value)} />
        </div>
        <div className="review-editable-field">
          <label className="review-field-key">Gender</label>
          <input className="review-field-input" value={gender} onChange={(e) => setGender(e.target.value)} />
        </div>
        <div className="review-editable-field">
          <label className="review-field-key">Room <span className="review-required">*</span></label>
          <input className="review-field-input" value={room} onChange={(e) => setRoom(e.target.value)} />
        </div>
      </div>
      <div className="chat-card-actions">
        <button
          className="generate-button"
          disabled={!canContinue}
          onClick={() => onContinue({
            caseName: caseName.trim(),
            mrn: mrn.trim(),
            patientName: patientName.trim(),
            age: age.trim(),
            gender: gender.trim(),
            room: room.trim(),
          })}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function CasePickerCard({ cases, onSelect, selectingId }) {
  if (!cases || cases.length === 0) {
    return (
      <div className="chat-card">
        <p className="chat-card-hint">No existing cases yet — try one of the other options instead.</p>
      </div>
    );
  }
  return (
    <div className="chat-card case-picker-card">
      <p className="chat-card-hint">Pick a case to duplicate — you'll get an editable copy to adjust before adding.</p>
      <div className="case-picker-list">
        {cases.map((c) => (
          <button
            key={c.id}
            className="case-picker-row"
            onClick={() => onSelect(c)}
            disabled={selectingId === c.id}
          >
            <span className="case-picker-row-name">{c.name}</span>
            <span className="case-picker-row-meta">
              {selectingId === c.id
                ? 'Loading...'
                : (c.configured ? 'Checklist Configured' : 'Checklist Pending')}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function FileUploadCard({ onContinue, uploading }) {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const addFiles = (incoming) => {
    const valid = Array.from(incoming).filter((f) =>
      ACCEPTED.some((ext) => f.name.toLowerCase().endsWith(ext))
    );
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      return [...prev, ...valid.filter((f) => !existing.has(f.name))];
    });
  };

  const removeFile = (name) => setFiles((prev) => prev.filter((f) => f.name !== name));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <div className="chat-card">
      <div
        className={`drop-zone ${dragOver ? 'drop-zone-active' : ''} ${files.length > 0 ? 'drop-zone-has-files' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.doc"
          style={{ display: 'none' }}
          onChange={(e) => addFiles(e.target.files)}
        />
        <div className="drop-zone-icon">📁</div>
        <p className="drop-zone-primary">Drag files here or click to browse</p>
        <p className="drop-zone-secondary">Accepts PDF and Word documents (.pdf, .docx)</p>
      </div>

      {files.length > 0 && (
        <div className="file-list">
          <span className="file-list-label">{files.length} file{files.length !== 1 ? 's' : ''} selected</span>
          <div className="file-chips">
            {files.map((f) => <FileChip key={f.name} file={f} onRemove={removeFile} />)}
          </div>
        </div>
      )}

      <div className="chat-card-actions">
        <button
          className="generate-button"
          onClick={() => onContinue(files)}
          disabled={files.length === 0 || uploading}
        >
          {uploading ? 'Reading files...' : 'Continue'}
        </button>
      </div>
    </div>
  );
}

function IndicatorPickerCard({ onContinue, recommendedCodes, recommendationsLoading }) {
  const {
    subCompetencies: AACN_SUB_COMPETENCIES,
    indicatorDomains: AACN_INDICATOR_DOMAINS,
    loading: aacnLoading,
  } = useContext(AacnContext);
  const [selected, setSelected] = useState([]);

  const toggle = (code) => setSelected((prev) =>
    prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
  );

  const renderIndicatorRow = (ind) => (
    <label
      key={ind.subCompetencyCode}
      className={`indicator-row ${selected.includes(ind.subCompetencyCode) ? 'indicator-row-selected' : ''}`}
    >
      <input
        type="checkbox"
        checked={selected.includes(ind.subCompetencyCode)}
        onChange={() => toggle(ind.subCompetencyCode)}
        style={{ display: 'none' }}
      />
      <span className="indicator-row-text">
        <span className="indicator-row-code">{ind.subCompetencyCode}</span> {ind.subCompetency}
      </span>
    </label>
  );

  const recommendedSet = new Set(recommendedCodes || []);
  const recommended = AACN_SUB_COMPETENCIES.filter((i) => recommendedSet.has(i.subCompetencyCode));

  return (
    <div className="chat-card indicator-picker-card">
      <p className="chat-card-hint">Pick the AACN sub-competencies this case should be graded on, or skip and just click Continue.</p>
      {aacnLoading && (
        <p className="chat-card-hint indicator-recommendations-loading">Loading AACN sub-competencies...</p>
      )}
      {!aacnLoading && recommendationsLoading && recommended.length === 0 && (
        <p className="chat-card-hint indicator-recommendations-loading">Fetching recommended sub-competencies for this case...</p>
      )}
      <div className="indicator-domain-list">
        {recommended.length > 0 && (
          <div className="indicator-domain-group indicator-recommended-group">
            <div className="indicator-domain-title">
              Recommended <span className="indicator-recommended-badge">Suggested for this case</span>
            </div>
            {recommended.map(renderIndicatorRow)}
          </div>
        )}
        {AACN_INDICATOR_DOMAINS.map((domainName) => {
          const entries = AACN_SUB_COMPETENCIES.filter(
            (i) => i.domainName === domainName && !recommendedSet.has(i.subCompetencyCode)
          );
          if (entries.length === 0) return null;
          return (
            <div key={domainName} className="indicator-domain-group">
              <div className="indicator-domain-title">{domainName}</div>
              {entries.map(renderIndicatorRow)}
            </div>
          );
        })}
      </div>
      <div className="chat-card-actions">
        <button className="generate-button" onClick={() => onContinue(selected)}>
          {selected.length > 0 ? `Continue with ${selected.length} selected` : 'Continue'}
        </button>
        {selected.length > 0 && (
          <button className="upload-cancel-btn" onClick={() => onContinue([])}>Skip</button>
        )}
      </div>
    </div>
  );
}

function CaseDraftPanel({ draft, fetchCases, onApproved, existingMrns }) {
  const [editedDraft, setEditedDraft] = useState(draft);
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [approving, setApproving] = useState(false);

  // A fresh Generate click replaces the draft prop with a new object — resync
  // so the panel reflects the newest generation rather than stale local edits.
  useEffect(() => {
    setEditedDraft(draft);
    setFieldErrors({});
    setErrorMsg('');
  }, [draft]);

  useEffect(() => {
    const dob = editedDraft?.patient?.dateOfBirth;
    if (!dob) return;
    const birth = new Date(dob);
    if (isNaN(birth.getTime())) return;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    setPatientField('age', String(age));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedDraft?.patient?.dateOfBirth]);

  const clearFieldError = (field) => setFieldErrors((prev) => {
    if (!prev[field]) return prev;
    const next = { ...prev };
    delete next[field];
    return next;
  });

  const setDraftField = (field, value) => {
    setEditedDraft((prev) => ({ ...prev, [field]: value }));
    clearFieldError(field);
  };
  const setPatientField = (field, value) => {
    setEditedDraft((prev) => ({ ...prev, patient: { ...prev.patient, [field]: value } }));
    clearFieldError(field);
  };
  const setReportItemContents = (index, value) => setEditedDraft((prev) => ({
    ...prev,
    reportItems: prev.reportItems.map((item, i) => i === index ? { ...item, contents: value } : item),
  }));

  const handleApprove = async () => {
    const validationErrors = validateApproveDraft(editedDraft);
    if (validationErrors) {
      setFieldErrors((prev) => ({ ...prev, ...validationErrors }));
      return;
    }

    setFieldErrors({});
    setErrorMsg('');
    setApproving(true);
    try {
      // New cases are always owned by whoever is creating them right now — never
      // whatever ownerEmail a duplicated-from case happened to carry.
      await saveCase({ ...editedDraft, ownerEmail: getCurrentUserEmail() });
      fetchCases();
      onApproved(editedDraft);
    } catch (err) {
      const { fieldErrors: routedFieldErrors, generalError } = routeApproveErrorMessage(err.response?.data?.message);
      if (routedFieldErrors?.mrn) {
        // MRN is auto-generated and no longer user-editable, so a collision (another
        // case grabbed the same number in the meantime) isn't something the user can
        // fix by hand — regenerate silently and let them just retry.
        setDraftField('mrn', generateMrn(existingMrns));
        setErrorMsg('That MRN was just taken by another case — generated a new one, please approve again.');
      } else {
        if (routedFieldErrors) setFieldErrors(routedFieldErrors);
        if (generalError) setErrorMsg(generalError);
      }
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="case-draft-form">
      <p className="review-intro">Review and edit anything, then approve to add it.</p>

      <div className="review-section">
        <label className="review-field-label">Case Name <span className="review-required">*</span></label>
        <input
          className="review-name-input"
          value={editedDraft.caseName || ''}
          onChange={(e) => setDraftField('caseName', e.target.value)}
          placeholder="Enter case name"
        />
      </div>

      <div className="review-section">
        <label className="review-field-label">MRN <span className="review-field-note">auto-generated — unique</span></label>
        <input
          className="review-name-input review-field-readonly"
          value={editedDraft.mrn || ''}
          readOnly
        />
      </div>

      <div className="review-section">
        <label className="review-field-label">Patient Information</label>
        <div className="review-fields-grid">
          {PATIENT_FIELDS.map(({ key, label, wide, required, type }) => (
            <div key={key} className={`review-editable-field ${wide ? 'review-field-wide' : ''}`}>
              <label className="review-field-key">{label}{required && <span className="review-required">*</span>}</label>
              <input
                className={`review-field-input ${key === 'age' ? 'review-field-readonly' : ''} ${fieldErrors[key] ? 'review-field-input-error' : ''}`}
                type={type || 'text'}
                value={editedDraft.patient?.[key] || ''}
                onChange={(e) => key !== 'age' && setPatientField(key, e.target.value)}
                readOnly={key === 'age'}
              />
              {fieldErrors[key] && <span className="review-field-error">{fieldErrors[key]}</span>}
            </div>
          ))}
        </div>
      </div>

      {editedDraft.reportItems?.length > 0 && (
        <div className="review-section">
          <label className="review-field-label">Clinical Report</label>
          <div className="report-items-list">
            {editedDraft.reportItems.map((item, i) => (
              <div key={item.name} className="report-item-field">
                <label className="report-item-label">{item.name}</label>
                <textarea
                  className="report-item-textarea"
                  value={item.contents || ''}
                  onChange={(e) => setReportItemContents(i, e.target.value)}
                  rows={2}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {editedDraft.filesProcessed?.length > 0 && (
        <div className="review-section">
          <label className="review-field-label">Files processed</label>
          <div className="review-files">
            {editedDraft.filesProcessed.map((name) => (
              <span key={name} className="review-file-chip">📄 {name}</span>
            ))}
          </div>
        </div>
      )}

      {errorMsg && <div className="upload-error-banner">{errorMsg}</div>}

      <div className="chat-card-actions">
        <button
          className="generate-button"
          onClick={handleApprove}
          disabled={!editedDraft.caseName?.trim() || !editedDraft.mrn?.trim() || !editedDraft.patient?.dateOfBirth?.trim() || approving}
        >
          {approving ? 'Saving...' : 'Approve & Add Case'}
        </button>
      </div>
    </div>
  );
}

function MicIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" fill="currentColor" />
      <path d="M19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.93V21H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-3.07A7 7 0 0 0 19 11Z" fill="currentColor" />
    </svg>
  );
}

function TypingIndicator() {
  return (
    <div className="chat-typing">
      <span className="chat-typing-dot" />
      <span className="chat-typing-dot" />
      <span className="chat-typing-dot" />
    </div>
  );
}

function renderMessageContent(message, handlers) {
  switch (message.type) {
    case 'path-choice':
      return message.resolved
        ? <div className="chat-resolved-note">Chosen: {PATH_CHOICE_LABELS[message.chosenPath] || message.chosenPath}</div>
        : <PathChoiceCard onChoose={(choice) => handlers.onPathChoice(message.id, choice)} />;
    case 'basics-form':
      return message.resolved
        ? <div className="chat-resolved-note">Basics captured.</div>
        : <BasicsFormCard
            onContinue={(values) => handlers.onBasicsContinue(message.id, values)}
            existingMrns={handlers.cases.map((c) => c.id)}
          />;
    case 'case-picker':
      return message.resolved
        ? <div className="chat-resolved-note">Duplicating: {message.selectedCaseName}</div>
        : (
          <CasePickerCard
            cases={handlers.cases}
            onSelect={(simCase) => handlers.onDuplicateCaseSelected(message.id, simCase)}
            selectingId={handlers.duplicatingCaseId}
          />
        );
    case 'file-upload':
      return message.resolved
        ? <div className="chat-resolved-note">Attached: {message.attachedFileNames?.join(', ') || 'no files'}</div>
        : <FileUploadCard onContinue={(files) => handlers.onFilesContinue(message.id, files)} uploading={handlers.uploadingFiles} />;
    case 'indicator-picker':
      return message.resolved
        ? (
          <div className="chat-resolved-note">
            {message.selectedCodes?.length
              ? `Selected ${message.selectedCodes.length} sub-competenc${message.selectedCodes.length === 1 ? 'y' : 'ies'}: ${message.selectedCodes.join(', ')}`
              : 'No specific sub-competencies selected'}
          </div>
        )
        : (
          <IndicatorPickerCard
            onContinue={(codes) => handlers.onIndicatorsContinue(message.id, codes)}
            recommendedCodes={message.recommendedCodes}
            recommendationsLoading={message.recommendationsLoading}
          />
        );
    case 'error':
      return <div className="upload-error-banner">{message.content}</div>;
    default:
      return <div className="chat-bubble-text">{message.content}</div>;
  }
}

function CaseCreationChat() {
  const navigate = useNavigate();
  const { cases, fetchCases } = useContext(CasesContext);

  const [messages, setMessages] = useState(createInitialMessages);
  const [stage, setStage] = useState('path-choice');
  const [path, setPath] = useState(null);
  const [basics, setBasics] = useState(null);
  const [situationDescription, setSituationDescription] = useState('');
  const [selectedIndicatorCodes, setSelectedIndicatorCodes] = useState([]);
  const [filesProcessed, setFilesProcessed] = useState([]);
  const [extractedFromFiles, setExtractedFromFiles] = useState(null);
  const [composerText, setComposerText] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [assistantTyping, setAssistantTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [duplicatingCaseId, setDuplicatingCaseId] = useState(null);
  const [rightPanelDraft, setRightPanelDraft] = useState(null);
  const [justApprovedName, setJustApprovedName] = useState(null);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  const idCounterRef = useRef(2);
  const transcriptRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const transcriptScrollRef = useRef(null);
  const prevMessageCountRef = useRef(messages.length);

  const nextId = () => `msg-${idCounterRef.current++}`;

  // The transcript no longer auto-scrolls (see chat-transcript below), so
  // flag new messages that arrived while the user was scrolled away from
  // the bottom rather than letting them silently go unnoticed off-screen.
  useEffect(() => {
    const el = transcriptScrollRef.current;
    if (el && messages.length > prevMessageCountRef.current) {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom > 80) {
        setHasNewMessage(true);
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length]);

  const handleTranscriptScroll = () => {
    const el = transcriptScrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 40) {
      setHasNewMessage(false);
    }
  };

  const scrollTranscriptToBottom = () => {
    const el = transcriptScrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    setHasNewMessage(false);
  };

  const appendMessage = (partial) => {
    const id = nextId();
    const message = { id, ...partial };
    setMessages((prev) => [...prev, message]);
    if (message.type === 'text') {
      transcriptRef.current = [...transcriptRef.current, { role: message.role, content: message.content }];
    }
    return id;
  };

  const updateMessage = (id, patch) => setMessages((prev) => prev.map((m) => m.id === id ? { ...m, ...patch } : m));

  // overrides lets callers pass freshly-known values (e.g. straight from a just-resolved
  // setState call or API response) instead of reading component state, which wouldn't have
  // re-rendered yet if this runs synchronously right after the setter.
  const beginIndicatorsStage = (overrides = {}) => {
    appendMessage({ role: 'assistant', type: 'text', content: "Now — let's cover AACN sub-competencies." });
    const pickerMsgId = appendMessage({ role: 'assistant', type: 'indicator-picker', resolved: false, recommendedCodes: [], recommendationsLoading: true });
    setStage('indicators');
    fetchRecommendedIndicators(pickerMsgId, overrides);
  };

  const fetchRecommendedIndicators = async (pickerMsgId, overrides = {}) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/case-chat/recommend-indicators`, {
        basics,
        situationDescription: overrides.situationDescription ?? situationDescription,
        extractedFromFiles: overrides.extractedFromFiles ?? extractedFromFiles,
      });
      updateMessage(pickerMsgId, { recommendedCodes: res.data?.recommendedCodes || [], recommendationsLoading: false });
    } catch {
      // Non-critical enhancement — the picker remains fully usable without recommendations.
      updateMessage(pickerMsgId, { recommendationsLoading: false });
    }
  };

  // Shared by ordinary chat replies and the explicit "Generate Case" button.
  // Whenever the backend hands back a caseDraft — whether from a regular
  // message or a Generate click — the right-hand panel updates with it; the
  // AI decides on its own when it has enough info via readyToReview, and the
  // backend only ever sends a non-null caseDraft once that's true.
  // overrides lets callers pass freshly-known values (e.g. straight from a just-resolved
  // setState call or API response) instead of reading component state, which wouldn't have
  // re-rendered yet if this runs synchronously right after the setter — same rationale as
  // beginIndicatorsStage/fetchRecommendedIndicators above.
  const sendMessageToAssistant = async ({ isGenerate, overrides = {} }) => {
    setAssistantTyping(true);
    const currentIndicatorCodes = overrides.selectedIndicatorCodes ?? selectedIndicatorCodes;
    const requestBody = {
      path,
      situationDescription: overrides.situationDescription ?? situationDescription,
      selectedIndicatorCodes: currentIndicatorCodes,
      filesProcessed: overrides.filesProcessed ?? filesProcessed,
      extractedFromFiles: overrides.extractedFromFiles ?? extractedFromFiles,
      transcript: transcriptRef.current,
    };
    try {
      const res = await sendChatTurn(requestBody);
      // The AI's own reply already explains what's missing when it's not ready (the
      // system prompt requires this) — appending our own generic "not enough detail"
      // message on top of that would just repeat the same rejection a second time,
      // which reads as jarring right after inviting the user to click Generate. Only
      // fall back to our own message if the AI genuinely returned nothing to show.
      if (res.reply) {
        appendMessage({ role: 'assistant', type: 'text', content: res.reply });
      } else if (isGenerate) {
        appendMessage({ role: 'assistant', type: 'error', content: "Not quite enough detail yet — add a bit more, then click \"Generate Case\" again." });
      }
      if (res.caseDraft) {
        setRightPanelDraft({
          ...mergeBasics(normalizeDraft(res.caseDraft), basics),
          indicatorCodes: currentIndicatorCodes,
        });
      }
    } catch {
      appendMessage({ role: 'assistant', type: 'error', content: 'Something went wrong — try again.' });
    } finally {
      setAssistantTyping(false);
    }
  };

  const runChatTurn = () => sendMessageToAssistant({ isGenerate: false });
  const handleGenerateCase = () => sendMessageToAssistant({ isGenerate: true });

  const handlePathChoice = (messageId, choice) => {
    updateMessage(messageId, { resolved: true, chosenPath: choice });
    appendMessage({ role: 'user', type: 'text', content: PATH_CHOICE_LABELS[choice] || choice });
    setPath(choice);
    if (choice === 'duplicate') {
      appendMessage({ role: 'assistant', type: 'text', content: 'Which case would you like to duplicate?' });
      appendMessage({ role: 'assistant', type: 'case-picker', resolved: false });
      setStage('case-picker');
    } else {
      appendMessage({ role: 'assistant', type: 'text', content: "First, let's get the basics." });
      appendMessage({ role: 'assistant', type: 'basics-form', resolved: false });
      setStage('basics');
    }
  };

  const handleBasicsContinue = (messageId, values) => {
    setBasics(values);
    updateMessage(messageId, { resolved: true });
    // Spell every field out explicitly (not just a human-readable summary) so
    // this actually lands in the AI's context — otherwise it has no way of
    // knowing age/gender were already given and will ask for them again.
    const basicsParts = [
      `Case Name: ${values.caseName || '—'}`,
      `MRN: ${values.mrn || '—'}`,
    ];
    if (values.patientName) basicsParts.push(`Patient Name: ${values.patientName}`);
    if (values.age) basicsParts.push(`Age: ${values.age}`);
    if (values.gender) basicsParts.push(`Gender: ${values.gender}`);
    basicsParts.push(`Room: ${values.room || '—'}`);
    appendMessage({ role: 'user', type: 'text', content: basicsParts.join(', ') });
    if (path === 'upload') {
      appendMessage({ role: 'assistant', type: 'text', content: "Great — drag in a PDF or Word document and I'll pull out the patient scenario for you." });
      appendMessage({ role: 'assistant', type: 'file-upload', resolved: false });
      setStage('file-upload');
    } else {
      appendMessage({ role: 'assistant', type: 'text', content: 'Tell me about the clinical situation you want to simulate — patient condition, setting, complexity, anything specific.' });
      setStage('situation');
    }
  };

  const handleDuplicateCaseSelected = async (messageId, simCase) => {
    setDuplicatingCaseId(simCase.id);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/cases/${simCase.id}`);
      const duplicated = normalizeDraft({
        ...res.data,
        caseName: (res.data.caseName || res.data.name || simCase.name) + ' (copy)',
        mrn: generateMrn(cases.map((c) => c.id)),
        patient: { ...res.data.patient, room: '' },
        // A duplicate is always a new custom case, even when duplicating a
        // standard (Kabilah-seeded) one — never carry the original's source.
        source: 'custom',
        // Likewise, never carry over the original's ownership — a duplicate is a
        // fresh draft owned by whoever duplicates it (set for real at save time).
        status: 'draft',
        ownerEmail: null,
        collaborators: [],
      });
      updateMessage(messageId, { resolved: true, selectedCaseName: simCase.name });
      appendMessage({ role: 'user', type: 'text', content: `Duplicate: ${simCase.name}` });
      appendMessage({ role: 'assistant', type: 'text', content: `Here's a copy of "${simCase.name}" on the right — review and edit anything, then approve when ready.` });
      setRightPanelDraft(duplicated);
      setStage('chatting');
    } catch {
      appendMessage({ role: 'assistant', type: 'error', content: 'Could not load that case. Please try again.' });
    } finally {
      setDuplicatingCaseId(null);
    }
  };

  const handleFilesContinue = async (messageId, files) => {
    setUploadingFiles(true);
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    try {
      const res = await axios.post(`${BACKEND_URL}/api/cases/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const names = files.map((f) => f.name);
      const processedNames = res.data.filesProcessed?.length > 0 ? res.data.filesProcessed : names;
      setExtractedFromFiles(res.data);
      setFilesProcessed(processedNames);
      updateMessage(messageId, { resolved: true, attachedFileNames: names });
      appendMessage({ role: 'user', type: 'text', content: `Attached: ${names.join(', ')}` });
      setUploadingFiles(false);
      // Let the AI actually react to what was extracted (it may flag something odd or
      // missing) instead of a canned "got it" — but the indicator question below always
      // follows regardless of what it says; indicator selection isn't contingent on
      // whether the case content itself is deemed ready yet.
      await sendMessageToAssistant({
        isGenerate: false,
        overrides: { extractedFromFiles: res.data, filesProcessed: processedNames },
      });
      beginIndicatorsStage({ extractedFromFiles: res.data });
    } catch {
      appendMessage({ role: 'assistant', type: 'error', content: 'Could not process the uploaded files. Please check the file types and try again.' });
      setUploadingFiles(false);
    }
  };

  const handleIndicatorsContinue = (messageId, selectedCodes) => {
    updateMessage(messageId, { resolved: true, selectedCodes });
    setSelectedIndicatorCodes(selectedCodes);
    const content = selectedCodes.length > 0
      ? `Target sub-competencies: ${selectedCodes.join(', ')}`
      : "Let's skip specific sub-competencies for now.";
    appendMessage({ role: 'user', type: 'text', content });
    setStage('chatting');
    // Let the AI react with what it actually still needs (if anything), now that it has
    // the situation/extracted-file context plus the indicators just given, instead of a
    // canned "whenever you're ready" line that may not reflect reality.
    sendMessageToAssistant({ isGenerate: false, overrides: { selectedIndicatorCodes: selectedCodes } });
  };

  const handleDraftApproved = (finalDraft) => {
    setJustApprovedName(finalDraft.caseName);
    setRightPanelDraft(null);
    appendMessage({ role: 'assistant', type: 'text', content: `✅ "${finalDraft.caseName}" has been added to your simulation cases.` });
  };

  const handleSend = () => {
    const text = composerText.trim();
    if (!text) return;
    setComposerText('');

    if (stage === 'situation') {
      setSituationDescription(text);
      appendMessage({ role: 'user', type: 'text', content: text });
      beginIndicatorsStage({ situationDescription: text });
      return;
    }

    appendMessage({ role: 'user', type: 'text', content: text });
    runChatTurn();
  };

  const transcribeAudio = async (blob) => {
    setTranscribing(true);
    const formData = new FormData();
    formData.append('audio', blob, `recording.${audioFileExtension(blob.type)}`);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/case-chat/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const text = res.data?.text?.trim();
      if (text) {
        setComposerText((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
      } else {
        appendMessage({ role: 'assistant', type: 'error', content: 'No speech detected — please try recording again.' });
      }
    } catch {
      appendMessage({ role: 'assistant', type: 'error', content: 'Could not transcribe audio. Please try again.' });
    } finally {
      setTranscribing(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      appendMessage({ role: 'assistant', type: 'error', content: 'Voice input is not supported in this browser.' });
      return;
    }
    try {
      // Disable Chrome's default WebRTC audio processing — on some hardware/driver
      // combinations, echo cancellation / noise suppression / auto-gain control can
      // over-suppress the signal down to near-silence even though capture "succeeds".
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });

      const track = stream.getAudioTracks()[0];
      // eslint-disable-next-line no-console
      console.log('[voice-diag] using input device:', track?.label || '(no label — permission not fully granted?)', track?.getSettings?.());

      // Diagnostic: sample the real-time signal level for the duration of the recording,
      // so we can tell from the console whether the captured stream actually carries audio
      // or is effectively silent — independent of encoding/container concerns.
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      const levelBuffer = new Uint8Array(analyser.frequencyBinCount);
      let peakLevel = 0;
      const levelIntervalId = setInterval(() => {
        analyser.getByteTimeDomainData(levelBuffer);
        let maxDeviation = 0;
        for (let i = 0; i < levelBuffer.length; i++) {
          maxDeviation = Math.max(maxDeviation, Math.abs(levelBuffer[i] - 128));
        }
        peakLevel = Math.max(peakLevel, maxDeviation);
      }, 100);

      const mimeType = pickSupportedAudioMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        clearInterval(levelIntervalId);
        audioCtx.close();
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        // eslint-disable-next-line no-console
        console.log('[voice-diag] recording finished', {
          blobSizeBytes: blob.size,
          blobType: blob.type,
          chunkCount: audioChunksRef.current.length,
          peakLevel0to128: peakLevel,
          note: peakLevel < 3 ? 'Peak level near zero — captured audio is effectively silent.' : 'Real signal was captured.',
        });
        transcribeAudio(blob);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[voice-diag] getUserMedia/MediaRecorder threw:', err);
      appendMessage({ role: 'assistant', type: 'error', content: 'Could not access the microphone. Please check permissions and try again.' });
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleStartAnother = () => {
    idCounterRef.current = 2;
    transcriptRef.current = [];
    setMessages(createInitialMessages());
    setStage('path-choice');
    setPath(null);
    setBasics(null);
    setSituationDescription('');
    setSelectedIndicatorCodes([]);
    setFilesProcessed([]);
    setExtractedFromFiles(null);
    setComposerText('');
    setUploadingFiles(false);
    setAssistantTyping(false);
    if (isRecording) stopRecording();
    setTranscribing(false);
    setDuplicatingCaseId(null);
    setRightPanelDraft(null);
    setJustApprovedName(null);
  };

  const hasUnresolvedCard = messages.some((m) =>
    ['path-choice', 'basics-form', 'file-upload', 'indicator-picker', 'case-picker'].includes(m.type) && !m.resolved
  );
  const composerDisabled = assistantTyping || uploadingFiles || hasUnresolvedCard;
  const composerPlaceholder = isRecording
    ? 'Listening...'
    : transcribing
      ? 'Transcribing...'
      : stage === 'situation'
        ? 'Describe the clinical situation...'
        : hasUnresolvedCard
          ? 'Resolve the step above to continue...'
          : 'Ask a follow-up or describe changes...';

  const handlers = {
    onPathChoice: handlePathChoice,
    onBasicsContinue: handleBasicsContinue,
    onFilesContinue: handleFilesContinue,
    onIndicatorsContinue: handleIndicatorsContinue,
    onDuplicateCaseSelected: handleDuplicateCaseSelected,
    fetchCases,
    uploadingFiles,
    cases,
    duplicatingCaseId,
  };

  return (
    <div className="root-container">
      <div className="title-container">
        <div className="title-left">
          <button className="back-button" onClick={() => navigate('/admin/cases')}>
            ← Cases
          </button>
          <div className="title-name-container" style={{ marginLeft: '16px' }}>
            <header className="name-header">Case Creation Chat</header>
          </div>
        </div>
      </div>

      <div className="component-container chat-page-component-container">
        <div className="chat-page-content">
          <div className="chat-left-panel">
            <div className="chat-transcript-wrap">
              <div className="chat-transcript" ref={transcriptScrollRef} onScroll={handleTranscriptScroll}>
                {messages.map((m) => (
                  <div key={m.id} className={`chat-row chat-row-${m.role}`}>
                    <div className={`chat-bubble chat-bubble-${m.role} ${m.type !== 'text' && m.type !== 'error' ? 'chat-bubble-card' : ''}`}>
                      {renderMessageContent(m, handlers)}
                    </div>
                  </div>
                ))}
                {assistantTyping && (
                  <div className="chat-row chat-row-assistant">
                    <div className="chat-bubble chat-bubble-assistant">
                      <TypingIndicator />
                    </div>
                  </div>
                )}
              </div>

              {hasNewMessage && (
                <button type="button" className="chat-new-message-arrow" onClick={scrollTranscriptToBottom}>
                  New message ↓
                </button>
              )}
            </div>

            <div className="chat-bottom-bar">
              <div className="chat-composer">
                <input
                  className="chat-composer-input"
                  value={composerText}
                  onChange={(e) => setComposerText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder={composerPlaceholder}
                  disabled={composerDisabled}
                />
                <button
                  type="button"
                  className="chat-mic-button"
                  onClick={handleMicClick}
                  disabled
                  title="Voice input is temporarily unavailable"
                >
                  <MicIcon />
                </button>
                <button
                  className="chat-send-button"
                  onClick={handleSend}
                  disabled={composerDisabled || !composerText.trim()}
                >
                  Send
                </button>
              </div>

              <div className="chat-generate-row">
                <button
                  className="generate-button chat-generate-button"
                  onClick={handleGenerateCase}
                  disabled={composerDisabled}
                >
                  {assistantTyping ? 'Working...' : 'Generate Case'}
                </button>
              </div>
            </div>
          </div>

          <div className="chat-right-panel">
            <div className="chat-right-panel-header">
              <span className="chat-right-panel-title">Case Draft</span>
            </div>
            {justApprovedName ? (
              <div className="upload-panel-centered case-draft-success">
                <div className="done-icon">✓</div>
                <p className="done-title">"{justApprovedName}" added</p>
                <p className="done-subtext">It's now in your simulation cases.</p>
                <div className="chat-card-actions">
                  <button className="generate-button" onClick={handleStartAnother}>Create Another Case</button>
                  <button className="upload-cancel-btn" onClick={() => { setLastCasesTab('draft'); navigate('/admin/cases'); }}>View Simulation Cases</button>
                </div>
              </div>
            ) : rightPanelDraft ? (
              <CaseDraftPanel
                draft={rightPanelDraft}
                fetchCases={fetchCases}
                onApproved={handleDraftApproved}
                existingMrns={cases.map((c) => c.id)}
              />
            ) : (
              <div className="case-draft-empty">
                <p className="chat-card-hint">Nothing generated yet.</p>
                <p className="chat-card-hint">Chat on the left, then click "Generate Case" — or duplicate an existing case to see it here.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CaseCreationChat;
